import TokenExchangeApi from './TokenExchangeApi'
import axios from 'axios'

/**
 * Interface pública para terceiros chamarem o fluxo de token-exchange.
 *
 * Exponho em window.FinxTokenExchange.exchange para integração simples sem
 * adicionar rotas de servidor. A função apenas delega para o serviço interno
 * `TokenExchangeApi.exchange`.
 *
 * Uso (exemplo):
 *  window.FinxTokenExchange.exchange('uuid-value').then(token => console.log(token))
 */
const Public = {
  async exchange(uuid: string, identificador?: string) {
    return TokenExchangeApi.exchange(uuid, identificador)
  },
  // exponha a função que grava o token no app para uso interno (token-exchange.vue chama isso)
  setAppUserFromExchange,
}



async function setAppUserFromExchange(tokenResp: any) {
  // Normaliza nomes (aceita camelCase ou snake_case)
  const accessToken = tokenResp.accessToken || tokenResp.access_token
  const refreshToken = tokenResp.refreshToken || tokenResp.refresh_token
  const expiresIn = tokenResp.expiresIn || tokenResp.expires_in
  const tokenType = tokenResp.tokenType || tokenResp.token_type || 'Bearer'

  if (!accessToken) throw new Error('exchange não retornou accessToken')

  // Log de depuração: não exibir token completo, apenas aviso de sucesso e dados não sensíveis
  try {
    const masked = typeof accessToken === 'string' ? `${accessToken.substr(0, 6)}...${accessToken.substr(-6)}` : String(accessToken)
    // eslint-disable-next-line no-console
  } catch (e) {
    // ignore logging failures
  }

  // Calcula expires_at no formato usado no projeto (segundos desde epoch)
  const nowSec = Math.floor(Date.now() / 1000)
  const expiresAt = expiresIn ? (nowSec + Number(expiresIn)) : nowSec + 3600

  // Monta o objeto user compatível com oidc-client (o callback.html usa userToken)
  const userToken: any = {
    access_token: accessToken,
    refresh_token: refreshToken || '',
    id_token: tokenResp.idToken || tokenResp.id_token || '',
    token_type: tokenType,
    expires_in: expiresIn,
    expires_at: expiresAt,
    scope: tokenResp.scope || '',
    profile: {
      // preencheremos abaixo com os dados do backend (roles, pageAuthorized, etc.)
      // colocar aqui os claims do id_token se você tiver (ex: email, name, sub)
    }
  }

  // Helper: decode JWT payload (no deps) — returns object or null
  function decodeJwtPayload(token: string | undefined) {
    if (!token || typeof token !== 'string') return null
    try {
      const parts = token.split('.')
      if (parts.length < 2) return null
      const payload = parts[1]
      // base64url -> base64
      const b64 = payload.replace(/-/g, '+').replace(/_/g, '/')
      // pad with '='
      const pad = b64.length % 4
      const padded = b64 + (pad ? '='.repeat(4 - pad) : '')
      const json = atob(padded)
      return JSON.parse(json)
    } catch (e) {
      // eslint-disable-next-line no-console
      return null
    }
  }

  // Preencher claims básicos a partir do id_token (se disponível) ou do access_token como fallback
  try {
    const idClaims = decodeJwtPayload(userToken.id_token || tokenResp.idToken || tokenResp.id_token)
    const accessClaims = decodeJwtPayload(tokenResp.access_token || tokenResp.accessToken)
    const claims = idClaims || accessClaims || tokenResp.raw || {}
    // mapear campos comuns
    if (claims) {
      userToken.profile.email = claims.email || claims.preferred_username || userToken.profile.email || ''
      userToken.profile.name = claims.name || claims.preferred_username || ''
      userToken.profile.preferredUsername = claims.preferred_username || claims.email || ''
      // some apps expect sub/sid
      userToken.profile.sub = claims.sub || userToken.profile.sub || ''
      userToken.profile.sid = claims.sid || userToken.profile.sid || ''
    }
  } catch (e) {
    // ignore — non-critical
  }

  // 1) buscar roles / dados do usuário no backend (mesma abordagem do callback.html)
  // NOTE: window.API_SEGURANCA deve estar definido (env-2.js)
  const rolesUrl = `${(window as any).API_SEGURANCA}/usuarios/roles`
  let userData: any = null
  try {
    const resp = await axios.get(rolesUrl, {
      headers: { Authorization: `Bearer ${accessToken}` },
      timeout: 5000,
    })
    userData = resp && resp.data
  } catch (err) {
    // opção: continuar sem roles (menos recomendado); aqui logamos o erro para depuração
    // eslint-disable-next-line no-console
    // continue sem userData ou rethrow — decida conforme política
  }

  // se vier userData coloque as propriedades necessárias no profile
  if (userData) {
    const { legacyRoles, legacyPageAuthorized, defaultPage, permissoesExtras } = userData.data || userData
    // defaultPage pode vir como string JSON ou já como objeto. Tratar ambos os casos.
    let parsedDefaultPage: any = {}
    try {
      if (defaultPage) {
        if (typeof defaultPage === 'string') {
          parsedDefaultPage = JSON.parse(defaultPage)
        } else if (typeof defaultPage === 'object') {
          parsedDefaultPage = defaultPage
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      parsedDefaultPage = {}
    }

    userToken.profile = {
      // claims básicos: (se tiver id_token, coloque decoded claims aqui)
      ...userToken.profile,
      roles: legacyRoles || [],                  // usado por muitas views
      pageAuthorized: legacyPageAuthorized || [],
      permissoes: permissoesExtras || {},
      defaultPage: parsedDefaultPage,
    }
  } else {
    // fallback: garanta arrays para evitar erros em check de roles/pageAuthorized
    userToken.profile.roles = userToken.profile.roles || []
    userToken.profile.pageAuthorized = userToken.profile.pageAuthorized || []
    userToken.profile.defaultPage = userToken.profile.defaultPage || {}
  }

  // 2) gravar no localStorage com a chave que o app usa
  const AUTH_KEY = `oidc.user:${(window as any).API_FINX_AUTH}:portal-client-2`

  localStorage.setItem(AUTH_KEY, JSON.stringify(userToken))

  // Também grave sob a chave que o oidc-client espera (used by @finx/web-repository Oidc)
  try {
    const oidcAuthority = `${(window as any).KEYCLOAK_AUTHORITY_URL}/realms/${(window as any).KEYCLOAK_REALM}`
    const oidcClientId = (window as any).KEYCLOAK_CLIENT_ID
    const OIDC_STORE_KEY = `oidc.user:${oidcAuthority}:${oidcClientId}`
    // eslint-disable-next-line no-console
    localStorage.setItem(OIDC_STORE_KEY, JSON.stringify(userToken))
  } catch (e) {
    // eslint-disable-next-line no-console
  }

  // 3) setar axios default header para requests subsequentes
  axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`

  // 4) atualizar Vue.prototype.$user para que views que acessam this.$user vejam as mudanças
  // (algumas infraestruturas do app leem esse valor apenas no bootstrap; forçar reload é simples)
  try {
    ;(window as any).__vue__ && ((window as any).__vue__.$user = userToken)
    // eslint-disable-next-line no-console
  } catch (e) {
    // ignora — depende de como a instância Vue está exposta
  }

  // Opcional: redirecionar para rota default
  return userToken
}

// Anexa no objeto global para que integrações de terceiros (JS injetado,
// widgets, etc.) possam chamar diretamente. Não sobrescreve se já existir.
try {
  const g = window as any
  if (!g.FinxTokenExchange) {
    g.FinxTokenExchange = Public
  }
} catch (e) {
  // ambiente sem window (SSR) — ignorar silenciosamente
}

export default Public
